import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { fetchTransportPreviewRate } from './api/transportRoutesApi';
import type { TransportPreviewRate } from '../../components/TransportCostSplitPreview';

/**
 * The PLN rate of the truck currency for the cost-split preview only
 * (decision 2026-10-04, F2). `savedRate` = the FX snapshot the server will
 * keep for this save (edit mode, see SharedTransportForm) — used as is, no
 * fetch. Otherwise today's NBP rate; a missing rate (e.g. before today's NBP
 * table is published) leaves the PLN column empty ("—"), never a guessed 1.
 */
export function useTransportPreviewRate(
  currency: string | undefined,
  enabled = true,
  savedRate: TransportPreviewRate | null = null,
): {
  rate: TransportPreviewRate | null;
  loading: boolean;
} {
  const code = currency || 'PLN';
  const [state, setState] = useState<{ code: string; rate: TransportPreviewRate | null; loading: boolean }>({
    code: '', rate: null, loading: false,
  });

  const fetchToday = enabled && !savedRate;
  useEffect(() => {
    if (!fetchToday || code === 'PLN') return undefined;
    let active = true;
    setState({ code, rate: null, loading: true });
    const date = dayjs().format('YYYY-MM-DD');
    Promise.resolve()
      .then(() => fetchTransportPreviewRate(code, date))
      .then((rate) => {
        if (active) setState({ code, rate, loading: false });
      })
      .catch(() => {
        if (active) setState({ code, rate: null, loading: false });
      });
    return () => { active = false; };
  }, [code, fetchToday]);

  if (code === 'PLN') return { rate: { rate: 1, effectiveDate: null }, loading: false };
  if (savedRate) return { rate: savedRate, loading: false };
  return state.code === code ? { rate: state.rate, loading: state.loading } : { rate: null, loading: enabled };
}
